import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import api from "@/lib/api";
import ProductImagesEditor from "./ProductImagesEditor";

const FIELD_DEFS = {
  products: [
    { key: "name", label: "Product Name", required: true },
    { key: "slug", label: "URL Slug" },
    { key: "category", label: "Category Slug", hint: "e.g. pass-boxes" },
    { key: "status", label: "Status", type: "select", options: ["published", "draft"] },
    { key: "short_description", label: "Short Description", type: "textarea", rows: 2 },
    { key: "long_description", label: "Long Description", type: "textarea", rows: 5 },
    { key: "image", label: "Main Image", type: "managed" },
    { key: "image_alt", label: "Main Image Alt Text", type: "managed" },
    { key: "gallery", label: "Image Gallery", type: "managed" },
    { key: "features", label: "Features (one per line)", type: "lines" },
    { key: "materials", label: "Materials (one per line)", type: "lines" },
    { key: "applications", label: "Applications (one per line)", type: "lines" },
    { key: "options", label: "Options (one per line)", type: "lines" },
    { key: "specs", label: 'Specs (one per line, "Key: Value")', type: "specs" },
    { key: "faqs", label: 'FAQs (one per line, "Q | A")', type: "faqs" },
  ],
  posts: [
    { key: "title", label: "Title", required: true },
    { key: "slug", label: "URL Slug" },
    { key: "category", label: "Category" },
    { key: "author", label: "Author" },
    { key: "date", label: "Date (YYYY-MM-DD)" },
    { key: "status", label: "Status", type: "select", options: ["published", "draft"] },
    { key: "excerpt", label: "Excerpt", type: "textarea", rows: 2 },
    { key: "image", label: "Featured Image URL" },
    { key: "content", label: "Content (one paragraph per line)", type: "lines" },
    { key: "tags", label: "Tags (one per line)", type: "lines" },
  ],
  industries: [
    { key: "name", label: "Industry Name", required: true },
    { key: "slug", label: "URL Slug" },
    { key: "intro", label: "Introduction", type: "textarea", rows: 2 },
    { key: "image", label: "Image URL" },
    { key: "challenges", label: "Challenges (one per line)", type: "lines" },
    { key: "solutions", label: "STJ Solutions (one per line)", type: "lines" },
    { key: "applications", label: "Applications (one per line)", type: "lines" },
    { key: "faqs", label: 'FAQs (one per line, "Q | A")', type: "faqs" },
  ],
  categories: [
    { key: "name", label: "Category Name", required: true },
    { key: "slug", label: "URL Slug" },
    { key: "order", label: "Sort Order (number)" },
    { key: "tagline", label: "Tagline" },
    { key: "description", label: "Description", type: "textarea", rows: 3 },
    { key: "image", label: "Image URL" },
  ],
  projects: [
    { key: "title", label: "Project Title", required: true },
    { key: "slug", label: "URL Slug" },
    { key: "industry", label: "Industry" },
    { key: "location", label: "Location" },
    { key: "status", label: "Status Label", hint: 'e.g. "Representative application"' },
    { key: "application", label: "Application", type: "textarea", rows: 2 },
    { key: "requirement", label: "Requirement", type: "textarea", rows: 2 },
    { key: "solution", label: "Solution", type: "textarea", rows: 3 },
    { key: "technical_details", label: "Technical Details", type: "textarea", rows: 2 },
    { key: "outcome", label: "Outcome", type: "textarea", rows: 2 },
    { key: "image", label: "Image URL" },
  ],
  knowledge: [
    { key: "title", label: "Article Title", required: true },
    { key: "slug", label: "URL Slug" },
    { key: "topic", label: "Topic", type: "select", options: ["Cleanroom Fundamentals", "Equipment Knowledge", "Technical Guides"] },
    { key: "author", label: "Author" },
    { key: "date", label: "Date (YYYY-MM-DD)" },
    { key: "status", label: "Status", type: "select", options: ["published", "draft"] },
    { key: "excerpt", label: "Excerpt", type: "textarea", rows: 2 },
    { key: "content", label: "Content (one paragraph per line)", type: "lines" },
    { key: "tags", label: "Tags (one per line)", type: "lines" },
    { key: "related_products", label: "Related Product Slugs (one per line)", type: "lines" },
  ],
  glossary: [
    { key: "term", label: "Term", required: true },
    { key: "definition", label: "Definition", type: "textarea", rows: 3 },
  ],
  resources: [
    { key: "title", label: "Title", required: true },
    { key: "slug", label: "URL Slug" },
    { key: "type", label: "Type", type: "select", options: ["Brochure", "Datasheet", "Catalogue", "Guide", "Technical Document"] },
    { key: "description", label: "Description", type: "textarea", rows: 2 },
    { key: "file_url", label: "File URL (leave empty for on-request)" },
    { key: "note", label: "Note (shown when no file URL)" },
  ],
};

const toText = (val, type) => {
  if (type === "managed") return val ?? "";
  if (type === "lines") return (val || []).join("\n");
  if (type === "specs") return (val || []).map((s) => `${s.key}: ${s.value}`).join("\n");
  if (type === "faqs") return (val || []).map((f) => `${f.q} | ${f.a}`).join("\n");
  return val ?? "";
};

const fromText = (text, type) => {
  if (type === "managed") return text;
  if (type === "lines") return text.split("\n").map((s) => s.trim()).filter(Boolean);
  if (type === "specs") return text.split("\n").map((l) => {
    const idx = l.indexOf(":");
    return idx > 0 ? { key: l.slice(0, idx).trim(), value: l.slice(idx + 1).trim() } : null;
  }).filter(Boolean);
  if (type === "faqs") return text.split("\n").map((l) => {
    const idx = l.indexOf("|");
    return idx > 0 ? { q: l.slice(0, idx).trim(), a: l.slice(idx + 1).trim() } : null;
  }).filter(Boolean);
  return text;
};

export default function ContentManager({ kind, title }) {
  const fields = FIELD_DEFS[kind];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // {id?, values:{}}

  const load = useCallback(() => {
    api.get(`/admin/${kind}`).then((r) => setItems(r.data)).catch(() => {});
  }, [kind]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    const values = {};
    fields.forEach((f) => { values[f.key] = f.type === "managed" && f.key === "gallery" ? [] : ""; });
    setEditing({ values });
  };

  const openEdit = (item) => {
    const values = {};
    fields.forEach((f) => { values[f.key] = toText(item[f.key], f.type); });
    setEditing({ id: item.id, values });
  };

  const save = async () => {
    const body = {};
    fields.forEach((f) => {
      const raw = editing.values[f.key];
      body[f.key] = fromText(raw ?? (f.type === "managed" && f.key === "gallery" ? [] : ""), f.type);
    });
    const mainField = kind === "products" ? "name" : kind === "glossary" ? "term" : "title";
    if (!body[mainField]) { toast.error(`${fields.find((f) => f.key === mainField).label} is required`); return; }
    try {
      if (editing.id) await api.put(`/admin/${kind}/${editing.id}`, body);
      else await api.post(`/admin/${kind}`, body);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch { toast.error("Could not save"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry? This cannot be undone.")) return;
    try { await api.delete(`/admin/${kind}/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <div data-testid={`content-manager-${kind}`}>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-semibold text-ink">{title}</h2>
        <button onClick={openNew} data-testid={`content-add-${kind}`}
          className="inline-flex items-center gap-1.5 bg-accent px-4 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-accent-dark">
          <Plus size={14} /> Add New
        </button>
      </div>

      <div className="mt-6 overflow-x-auto border border-line bg-white">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-line text-left">
              <th className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.15em] text-steel">Name</th>
              <th className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.15em] text-steel">Slug</th>
              <th className="px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.15em] text-steel">Status</th>
              <th className="px-5 py-3.5 text-right font-mono text-[10px] uppercase tracking-[0.15em] text-steel">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b border-line/60 last:border-0" data-testid={`content-row-${item.id}`}>
                <td className="px-5 py-3.5 font-medium text-ink">{item.name || item.title || item.term}</td>
                <td className="px-5 py-3.5 font-mono text-xs text-steel">{item.slug}</td>
                <td className="px-5 py-3.5">
                  <span className={`px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] ${item.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-mist text-steel"}`}>
                    {item.status || "—"}
                  </span>
                </td>
                <td className="px-5 py-3.5 text-right">
                  <button onClick={() => openEdit(item)} data-testid={`content-edit-${item.id}`} aria-label="Edit"
                    className="mr-2 inline-flex h-8 w-8 items-center justify-center border border-line text-steel hover:border-accent hover:text-accent">
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => remove(item.id)} data-testid={`content-delete-${item.id}`} aria-label="Delete"
                    className="inline-flex h-8 w-8 items-center justify-center border border-line text-steel hover:border-red-400 hover:text-red-600">
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!items.length && <p className="p-8 text-center text-sm text-steel">No entries yet.</p>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end bg-ink/50 backdrop-blur-sm" data-testid="content-editor-drawer">
          <div data-lenis-prevent className="h-full w-full max-w-xl overflow-y-auto overscroll-contain bg-white p-7 shadow-2xl">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-semibold text-ink">{editing.id ? "Edit" : "Add"} {title}</h3>
              <button onClick={() => setEditing(null)} data-testid="content-editor-close" aria-label="Close editor"
                className="flex h-9 w-9 items-center justify-center border border-line text-steel hover:text-ink">
                <X size={16} />
              </button>
            </div>
            <div className="mt-6 space-y-5">
              {fields.map((f) => {
                if (f.type === "managed") {
                  if (f.key !== "image") return null;
                  return (
                    <ProductImagesEditor key="_images" values={editing.values}
                      onChange={(patch) => setEditing({ ...editing, values: { ...editing.values, ...patch } })} />
                  );
                }
                return (
                <div key={f.key}>
                  <label htmlFor={`ce-${f.key}`} className="label-field">
                    {f.label} {f.required && "*"}
                  </label>
                  {f.type === "select" ? (
                    <select id={`ce-${f.key}`} data-testid={`editor-${f.key}`} className="input-field"
                      value={editing.values[f.key]} onChange={(e) => setEditing({ ...editing, values: { ...editing.values, [f.key]: e.target.value } })}>
                      <option value="">Select…</option>
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (f.type === "textarea" || f.type === "lines" || f.type === "specs" || f.type === "faqs") ? (
                    <textarea id={`ce-${f.key}`} data-testid={`editor-${f.key}`} rows={f.rows || 4} className="input-field font-mono text-xs"
                      value={editing.values[f.key]} onChange={(e) => setEditing({ ...editing, values: { ...editing.values, [f.key]: e.target.value } })} />
                  ) : (
                    <input id={`ce-${f.key}`} data-testid={`editor-${f.key}`} className="input-field"
                      value={editing.values[f.key]} onChange={(e) => setEditing({ ...editing, values: { ...editing.values, [f.key]: e.target.value } })} />
                  )}
                  {f.hint && <p className="mt-1 text-xs text-slate-400">{f.hint}</p>}
                </div>
                );
              })}
            </div>
            <button onClick={save} data-testid="content-editor-save"
              className="mt-8 w-full bg-accent py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-dark">
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
